import { initOptions } from './options';
import { recordMetric } from './newrelic';
import { FetchHttpError } from '../util/fetch-http-error';
import { getAbortSignal } from './abort';
import { sanitizeUrl } from '../util/sanitize';
import { ERROR_NAMES, HTTP_STATUS, DEFAULT_OPTIONS } from '../constants';

const validateResponse = async (response) => {
	if (!response.ok) {
		const { headers } = response;

		let error;
		let description;
		try {
			const contentType = headers.get('content-type') || '';
			const contentLength = headers.get('content-length');

			if (contentLength === '0') {
				description = response.statusText;
			} else if (contentType.includes('json')) {
				const text = await response.text();
				if (text) {
					try {
						error = JSON.parse(text);
						description =
							error.description || error.message || JSON.stringify(error);
					} catch (jsonErr) {
						description = text;
					}
				} else {
					description = response.statusText;
				}
			} else if (
				contentType.includes('text/') ||
				contentType.includes('html')
			) {
				const text = await response.text();
				description = text || response.statusText;
			} else {
				description = response.statusText;
			}
		} catch (e) {
			description = response.statusText;
		}

		throw new FetchHttpError(response, description, error);
	}
};

const defaultOptions = {
	...DEFAULT_OPTIONS.FETCH,
	validateResponse,
};

export const defaultIsRetryable = (err) =>
	!err.status ||
	err.status === HTTP_STATUS.TOO_MANY_REQUESTS ||
	Math.trunc(err.status / 100) === 5;

export const defaultCircuitBreakerErrorFilter = (err) =>
	!defaultIsRetryable(err);

export const contextKey = 'fetch';

export const getWrapper = (_nextAction, context) => {
	if (!fetch) {
		throw new Error(
			'No globally available `fetch` implementation could be found.',
		);
	}

	initOptions(context, contextKey, defaultOptions);

	return async (fnArgs, executionContext) => {
		const [url] = fnArgs;
		const signal = getAbortSignal(executionContext);
		const options = {
			...executionContext[contextKey].options,
			...(signal && { signal }),
		};
		const { responseBodyType } = options;

		const validResponseTypes = [
			'json',
			'text',
			'blob',
			'arrayBuffer',
			'formData',
		];
		if (
			responseBodyType !== null &&
			responseBodyType !== undefined &&
			!validResponseTypes.includes(responseBodyType)
		) {
			throw new TypeError(
				`Invalid responseBodyType: "${responseBodyType}". Must be one of: ${validResponseTypes.join(', ')}`,
			);
		}

		recordMetric(executionContext, `${contextKey}.url`, sanitizeUrl(url));

		let response;
		try {
			response = await fetch(url, options);
		} catch (err) {
			if (err.name === ERROR_NAMES.ABORT_ERROR) {
				return null;
			} else {
				throw err;
			}
		}

		const { status } = response;
		recordMetric(executionContext, `${contextKey}.status`, response.status);

		// response.json() throws an exception when there is no content.
		if (status === HTTP_STATUS.NO_CONTENT) {
			return null;
		}

		if (options.validateResponse !== null) {
			await options.validateResponse(response);
		}

		if (responseBodyType === null) {
			return response;
		} else {
			return response[responseBodyType]();
		}
	};
};

getWrapper.contextKey = contextKey;
